/**
 * Shared calculator state — syncs sqft, bedrooms, bathrooms, serviceType, and frequency
 * across PricingCalculator, CustomPlanBuilder, and SavingsCalculator via localStorage
 * + a custom browser event so all mounted instances update in real-time.
 */
import { useState, useEffect, useCallback } from "react";

const STORAGE_KEY = "uplevel_calc_state";
const SYNC_EVENT = "uplevel_calc_sync";

export interface CalcState {
  sqft: number;
  bedrooms: number;
  bathrooms: number;
  serviceType: "standard" | "deep" | "move" | "construction";
  /** Unified frequency key used everywhere: onetime | weekly | biweekly | triweekly | monthly */
  frequency: "onetime" | "weekly" | "biweekly" | "triweekly" | "monthly";
}

const DEFAULTS: CalcState = {
  sqft: 0,
  bedrooms: 0,
  bathrooms: 0,
  serviceType: "standard",
  frequency: "biweekly",
};

function readStorage(): CalcState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULTS };
  }
}

function writeStorage(state: CalcState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    // Broadcast to all other instances on this page
    window.dispatchEvent(new CustomEvent(SYNC_EVENT, { detail: state }));
  } catch {}
}

export function useCalculatorState() {
  const [state, setState] = useState<CalcState>(readStorage);

  // Listen for changes from other instances
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<CalcState>).detail;
      setState(detail);
    };
    window.addEventListener(SYNC_EVENT, handler);
    return () => window.removeEventListener(SYNC_EVENT, handler);
  }, []);

  const update = useCallback((partial: Partial<CalcState>) => {
    setState(prev => {
      const next = { ...prev, ...partial };
      writeStorage(next);
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    setState(DEFAULTS);
    writeStorage(DEFAULTS);
  }, []);

  return { state, update, reset };
}
